import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const STATS = [
    { n: "22", l: "Pieces / Year" },
    { n: "242", l: "Bahamas — Origin" },
    { n: "02", l: "Manufacturing Partners" },
    { n: "2026", l: "Founded — Nassau" },
];

const pillars = [
    {
        n: "01",
        title: "Founded in Nassau",
        body: "Born in the Bahamas in 2026 — a brand built on faith, discipline, and ambition. From the islands to the world, every collection carries that origin.",
    },
    {
        n: "02",
        title: "Made in China & Japan",
        body: "We work with two specialist manufacturing partners. Tees, knitwear, outerwear, footwear and accessories in China; selvedge denim and the heaviest cottons in Japan. Pieces are inspected at source before shipping.",
    },
    {
        n: "03",
        title: "International influences",
        body: "Tokyo silhouettes. Italian tailoring. New York streetwear. Antwerp restraint. Every drop is the answer to questions we've collected from a decade of looking at clothes.",
    },
    {
        n: "04",
        title: "Built on details",
        body: "The interior of a seam. The weight of a label. The way a pocket sits when the hand is empty. The details others skip are the reason we started in the first place.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page" className="pt-28">
            {/* Manifesto */}
            <section className="container-rail py-16 border-b border-white/5">
                <div className="grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-4">
                        <div className="text-overline text-[#A3A3A3]">N° 04 — The Studio</div>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <h1 className="text-display text-5xl sm:text-6xl md:text-8xl leading-[0.92]">
                            A small studio. <br />
                            <span className="text-display-italic text-[#A3A3A3]">Big patience.</span>
                        </h1>
                        <div className="text-[#A3A3A3] text-lg leading-relaxed mt-10 max-w-2xl space-y-5">
                            <p>
                                SAVNT was founded in Nassau, Bahamas, in 2026 with one purpose: to create timeless streetwear for those set apart.
                            </p>
                            <p>
                                Built on faith, discipline, and ambition, every piece is designed with intention and meaning. We don't chase trends — we create statements that outlive them.
                            </p>
                            <p>
                                SAVNT represents resilience, purpose, and the courage to walk your own path. From the Bahamas to the world, this is more than clothing — it's a mindset.
                            </p>
                            <p className="text-[#F4F4F0]">
                                For those who lead, endure, and refuse to settle.
                            </p>
                            <p className="text-display text-2xl text-[#F4F4F0] pt-2">
                                SAVNT — <span className="text-display-italic text-[#A3A3A3]">For The Chosen.</span>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Editorial image */}
            <section className="container-rail py-16">
                <div className="relative aspect-[16/8] overflow-hidden bg-[#141414] border border-white/[0.06]">
                    <img
                        src="https://images.unsplash.com/photo-1603189343302-e603f7add05a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZmFzaGlvbiUyMGVkaXRvcmlhbCUyMHN0cmVldHdlYXJ8ZW58MHx8fHwxNzgxMjM2ODk5fDA&ixlib=rb-4.1.0&q=85"
                        alt="Studio editorial"
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40" />
                    <div className="absolute bottom-6 left-6 text-overline text-white/90">Editorial — Nassau, FW26</div>
                </div>
            </section>

            {/* Pillars */}
            <section className="container-rail py-20">
                <div className="text-overline text-[#A3A3A3] mb-12">Our four pillars</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-x-16 md:gap-y-14">
                    {pillars.map((p) => (
                        <article key={p.n} className="relative pt-6 border-t border-white/10">
                            <div className="text-overline text-[#591C1C] absolute -top-2.5 left-0 bg-[#0C0C0C] pr-3">
                                — {p.n}
                            </div>
                            <h3 className="text-display text-3xl mb-3">{p.title}</h3>
                            <p className="text-[#A3A3A3] leading-relaxed">{p.body}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Numbers strip */}
            <section className="bg-[#0A0A0A] border-y border-white/5">
                <div className="container-rail py-16 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
                    {[
                        { n: "22", l: "Pieces / Year" },
                        { n: "242", l: "Bahamas — Origin" },
                        { n: "02", l: "Manufacturing Partners" },
                        { n: "2026", l: "Founded — Nassau" },
                    ].map((s, i) => (
                        <div key={s.n}>
                            <div className="text-display text-5xl sm:text-6xl">{s.n}</div>
                            <div className="text-overline text-[#A3A3A3] mt-3">{s.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="container-rail py-24 text-center space-y-8">
                <h2 className="text-display text-4xl sm:text-5xl">Come find a piece worth keeping.</h2>
                <Link to="/shop" className="btn-primary inline-flex" data-testid="about-shop-cta">
                    Enter the Shop <ArrowRight className="h-4 w-4" />
                </Link>
            </section>
        </div>
    );
}
